import createCachedSelector from 're-reselect';
import { createSelector } from '@reduxjs/toolkit';

import { ReduxState } from 'app/redux/types';

import { DictionaryData } from 'app/lang';
import { StakeType } from 'app/lib/staking/constants';

import {
  BET_ERROR_DICTIONARY,
  BetErrorCode, MATCH_ERROR_DICTIONARY,
  MatchErrorCode, OperatorRefusedCode,
  StakeErrorCode, SubmissionState,
} from '../constants';
import {
  RecordOfBetEntryType,
  RecordOfEntryErrorType,
  RecordOfMatchEntryType,
  RecordOfStakeEntryType,
} from '../records';
import {
  EntriesBetIds,
  EntriesBets,
  EntriesDeletes,
  EntriesInserts,
  EntriesMatches,
  EntriesSports,
  EntriesStakes,
  EntriesState,
} from '../types/entries';
import { selectCurrentStakeType } from './stakeTypes';
import { selectBetInfoSubmissionByBetId } from './stakeTypes/single';
import {
  selectEntriesState,
  selectExpressStakeTypeState,
  selectSingleStakeTypeState,
  selectSubmissionState,
} from './state';

// ---

export const selectInsertsQueue = (state: ReduxState): EntriesInserts => {
  return selectEntriesState(state).get('inserts');
};

export const selectDeletesQueue = (state: ReduxState): EntriesDeletes => {
  return selectEntriesState(state).get('deletes');
};

export const selectBets = (state: ReduxState): EntriesBets => {
  return selectEntriesState(state).get('bets');
};

export const selectBetIds = (state: ReduxState): EntriesBetIds => {
  return selectEntriesState(state).get('betIds');
};

export const selectStakes = (state: ReduxState): EntriesStakes => {
  return selectEntriesState(state).get('stakes');
};

export const selectMatches = (state: ReduxState): EntriesMatches => {
  return selectEntriesState(state).get('matches');
};

export const selectSports = (state: ReduxState): EntriesSports => {
  return selectEntriesState(state).get('sports');
};

export const selectTotalAmountActive = (state: ReduxState): boolean => {
  return selectEntriesState(state).get('totalAmountActive');
};

export const selectLastAddedBet = (state: ReduxState): EntriesState['lastAddedBet'] => {
  return selectEntriesState(state).get('lastAddedBet');
};

const selectMatchIdsWithOneBet = createSelector(
  selectMatches,
  (matches) => {
    const matchIdsWithOneBet = new Set();

    matches.forEach((match) => {
      const isExpressInsideMatch = match.betIds.size > 1;

      if (!isExpressInsideMatch) {
        matchIdsWithOneBet.add(match.entity.id);
      }
    });

    return matchIdsWithOneBet;
  },
);

export const selectHasPriceChanges = createSelector(
  (state: ReduxState) => selectExpressStakeTypeState(state).matchPrices,
  selectBets,
  selectMatchIdsWithOneBet,
  (matchPrices, bets, matchIdsWithOneBet) => {
    let hasChanges = matchPrices.some(({ entryPrice, entityPrice }, matchId) => {
      if (matchIdsWithOneBet.has(matchId)) {
        return false;
      }

      return (
        entityPrice !== undefined && entityPrice !== entryPrice
      );
    });

    if (!hasChanges) {
      hasChanges = bets.some(({ entity, price }) => entity.price !== price);
    }

    return hasChanges;
  },
);

// ---

export function selectHasEntryError(entryError?: RecordOfEntryErrorType | null): boolean {
  if (!entryError) {
    return false;
  }
  const reason = entryError?.rejectReason?.reason;

  return !(
    entryError.code === StakeErrorCode.NOT_ENOUGH_MONEY
    || entryError.code === StakeErrorCode.INTERNAL_SYSTEM_ERROR
    || entryError.code === BetErrorCode.PRICE_CHANGED
    || (entryError.code === StakeErrorCode.BET_PRICE_CHANGED
    && reason === OperatorRefusedCode.READY_TO_ACCEPT_ONLY_AMOUNT)
  );
}

export const selectRequestErrorShowing = createCachedSelector(
  (state: ReduxState, betId: number) => (
    selectBetInfoSubmissionByBetId(state, betId) === SubmissionState.AWAITING
  ),
  (state: ReduxState, betId: number) => (
    selectSingleStakeTypeState(state).betsInfo.get(betId)?.error
  ),
  (isAwaiting, error) => {
    if (!isAwaiting) {
      return false;
    }

    return selectHasEntryError(error);
  },
)((_state: ReduxState, betId: number) => betId);

// ---

const isLockedEntry = (
  match?: RecordOfMatchEntryType,
  stake?: RecordOfStakeEntryType,
  bet?: RecordOfBetEntryType,
) => {
  return !(match?.entity.isOpen && stake?.entity.isOpen && bet?.entity.isOpen);
};

/**
 * Returns `true` if entry in the bet slip has "locked" state.
 */
export const selectIsLockedEntry = createCachedSelector(
  (state: ReduxState, betId: number) => selectBets(state).get(betId),
  (state: ReduxState, betId: number) => {
    const bet = selectBets(state).get(betId);

    return bet ? selectStakes(state).get(bet?.entity.stakeId) : undefined;
  },
  (state: ReduxState, betId: number) => {
    const bet = selectBets(state).get(betId);

    return bet ? selectMatches(state).get(bet?.entity.matchId) : undefined;
  },
  (bet, stake, match) => {
    return isLockedEntry(match, stake, bet);
  },
)((_state: ReduxState, betId: number) => betId);

/**
 * Returns `true` if every entry in the bet slip has "locked" state.
 */
export const selectEveryHasLockedState = createSelector(
  selectBets,
  selectStakes,
  selectMatches,
  (bets, stakes, matches) => {
    return bets.every((bet) => {
      const stake = stakes.get(bet.entity.stakeId);
      const match = matches.get(bet.entity.matchId);

      return isLockedEntry(match, stake, bet);
    });
  },
);

/**
 * Returns `true` if any entry in the bet slip has "locked" state.
 */
export const selectAnyHasLockedState = createSelector(
  selectBets,
  selectStakes,
  selectMatches,
  (bets, stakes, matches) => {
    return bets.some((bet) => {
      const stake = stakes.get(bet.entity.stakeId);
      const match = matches.get(bet.entity.matchId);

      return isLockedEntry(match, stake, bet);
    });
  },
);

/**
 * Returns `true` if some entry in the bet slip has "error".
 */
export const selectSomeHasError = createSelector(
  (state: ReduxState) => selectSubmissionState(state) === SubmissionState.AWAITING,
  (state: ReduxState) => selectSingleStakeTypeState(state).betsInfo,
  (isAwaiting, betsInfo) => {
    if (!isAwaiting) {
      return false;
    }

    return betsInfo.some((bet) => selectHasEntryError(bet.error));
  },
);

// ---

export const selectLockedBetErrorMessage = (
  state: ReduxState,
  betId: number,
): DictionaryData | null => {
  const bet = selectBets(state).get(betId)?.get('entity');

  if (!bet?.isOpen) {
    return BET_ERROR_DICTIONARY[BetErrorCode.RECEPTION_CLOSED];
  }

  const stake = selectStakes(state).get(bet.stakeId)?.get('entity');

  if (!stake?.isOpen) {
    return BET_ERROR_DICTIONARY[BetErrorCode.RECEPTION_CLOSED];
  }

  const match = selectMatches(state).get(stake.matchId)?.get('entity');

  if (!match?.isOpen) {
    return MATCH_ERROR_DICTIONARY[MatchErrorCode.RECEPTION_CLOSED];
  }

  return null;
};

export const selectAllUsedFreeBets = createSelector(
  [
    (state: ReduxState) => selectSingleStakeTypeState(state).betsInfo,
    (state: ReduxState) => selectExpressStakeTypeState(state).freeBet,
    selectCurrentStakeType,
  ],
  (betsInfo, expressFreeBet, currentStakeType) => {
    const freeBetIds = new Set<number>();

    if (currentStakeType.has(StakeType.SINGLE)) {
      betsInfo.forEach((bet) => {
        if (bet.freeBet) {
          freeBetIds.add(bet.freeBet.id);
        }
      });
    }

    if (currentStakeType.has(StakeType.EXPRESS) && expressFreeBet) {
      freeBetIds.add(expressFreeBet.id);
    }

    return freeBetIds;
  },
);

export const selectCanUseFreeBet = createCachedSelector(
  (_state: ReduxState, betId: number) => betId,
  (state: ReduxState) => selectSingleStakeTypeState(state).betsInfo,
  (betId, betsInfo) => {
    const matchId = betsInfo.get(betId)?.get('matchId');

    if (!matchId) {
      return true;
    }
    for (const entry of betsInfo.values()) {
      if (entry.matchId === matchId && Boolean(entry.freeBet)) {
        return false;
      }
    }

    return true;
  },
)((_state, betId) => betId);

export const selectAllUsedPledgeStakes = createSelector(
  (state: ReduxState) => selectSingleStakeTypeState(state).betsInfo,
  (betsInfo) => {
    const pledgeStakeIds = new Set<number>();

    return betsInfo.reduce((acc, bet) => {
      if (bet.pledgeStake) {
        acc.add(bet.pledgeStake.stakeId);
      }

      return acc;
    }, pledgeStakeIds);
  },
);
